// Package imports:
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket } from '@fortawesome/pro-solid-svg-icons';

interface BannerPreview {
    url: string;
    name: string;
    width: number;
    height: number;
    size: number;
}

interface IKYCSettingsBannerUploadProps {
    bannerPreview: BannerPreview | null;
    setBannerPreview: (preview: BannerPreview | null) => void;
    bannerError: string;
    setBannerError: (error: string) => void;
    setBannerFile: (file: File | null) => void;
}

const MAX_WIDTH = 600;
const MAX_HEIGHT = 300;
const MAX_SIZE_KB = 150;
const MAX_SIZE_BYTES = MAX_SIZE_KB * 1024;
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

const KYCSettingsBannerUpload: React.FC<IKYCSettingsBannerUploadProps> = ({
    bannerPreview,
    setBannerPreview,
    bannerError,
    setBannerError,
    setBannerFile,
}) => {
    const isAcceptedFileType = useCallback((file: File): boolean => {
        const extension = '.' + file.name.split('.').pop()?.toLowerCase();
        return ACCEPTED_EXTENSIONS.includes(extension) || ACCEPTED_MIME_TYPES.includes(file.type);
    }, []);

    const readFileAsDataURL = useCallback((file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Ekki tókst að lesa skrá.'));
            reader.readAsDataURL(file);
        });
    }, []);

    const validateImage = useCallback((file: File, dataUrl: string): Promise<{ valid: boolean; width: number; height: number; error?: string }> => {
        return new Promise((resolve) => {
            if (!isAcceptedFileType(file)) {
                const extension = file.name.split('.').pop()?.toUpperCase() || 'óþekkt';
                resolve({
                    valid: false,
                    width: 0,
                    height: 0,
                    error: `Skráargerð .${extension} er ekki studd. Vinsamlegast notaðu JPG, JPEG eða PNG.`,
                });
                return;
            }

            const img = new Image();
            img.onload = () => {
                const errors: string[] = [];
                const fileSizeKB = Math.round(file.size / 1024);

                if (img.width > MAX_WIDTH || img.height > MAX_HEIGHT) {
                    errors.push(
                        `Myndinn er ${img.width} x ${img.height} px en hámark er ${MAX_WIDTH} x ${MAX_HEIGHT} px.`
                    );
                }

                if (file.size > MAX_SIZE_BYTES) {
                    errors.push(
                        `Skráarstærð er ${fileSizeKB} kb en hámark er ${MAX_SIZE_KB} kb.`
                    );
                }

                if (errors.length > 0) {
                    resolve({
                        valid: false,
                        width: img.width,
                        height: img.height,
                        error: errors.join(' '),
                    });
                } else {
                    resolve({ valid: true, width: img.width, height: img.height });
                }
            };
            img.onerror = () => {
                resolve({ valid: false, width: 0, height: 0, error: 'Ekki tókst að lesa mynd. Vinsamlegast reyndu aðra skrá.' });
            };
            img.src = dataUrl;
        });
    }, [isAcceptedFileType]);

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];

        let dataUrl: string;
        try {
            dataUrl = await readFileAsDataURL(file);
        } catch {
            setBannerError('Ekki tókst að lesa skrá. Vinsamlegast reyndu aftur.');
            return;
        }

        const result = await validateImage(file, dataUrl);

        const preview: BannerPreview = {
            url: dataUrl,
            name: file.name,
            width: result.width,
            height: result.height,
            size: file.size,
        };

        setBannerPreview(preview);
        setBannerFile(result.valid ? file : null);

        if (!result.valid) {
            setBannerError(result.error || '');
        } else {
            setBannerError('');
        }
    }, [readFileAsDataURL, validateImage, setBannerPreview, setBannerFile, setBannerError]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false,
        accept: { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] },
    });

    // Don't show dropzone if there's a valid banner already uploaded
    if (bannerPreview && !bannerError) {
        return null;
    }

    return (
        <div
            {...getRootProps()}
            className={cx('kyc-settings__dropzone', {
                'kyc-settings__dropzone--active': isDragActive,
                'kyc-settings__dropzone--error': !!bannerError,
            })}
        >
            <input {...getInputProps()} />
            <FontAwesomeIcon
                icon={faArrowUpFromBracket}
                className="kyc-settings__dropzone-icon"
            />
            <div className="kyc-settings__dropzone-text">
                <p className="kyc-settings__dropzone-instruction">Smelltu hér eða dragðu</p>
                <p className="kyc-settings__dropzone-filetypes">JPG, JPEG, PNG</p>
                <p className="kyc-settings__dropzone-instruction">myndina þína á þetta svæði.</p>
            </div>
        </div>
    );
};

export default KYCSettingsBannerUpload;
